import React, { useState } from 'react';
import { formatSwissAmount, parseSwissAmount, EM_DASH } from './formatSwiss';

// Numeric input for CHF amounts.
//
// Display state (no draft) : Swiss-formatted with ASCII apostrophe, or em-dash
// for zero/empty values. Edit state (focused) : raw digits for ergonomic typing.
// onCommit fires on blur or Enter, but only when the parsed value differs from
// the prop. Escape reverts the draft and blurs without commit.
//
// Visual styling (color, borders, monospace, alignment) is delegated to CSS
// via the `billing-amount-input` class plus any extra className.
export default function AmountInput({
  value,
  onCommit,
  disabled = false,
  align = 'right',
  className = '',
  placeholder = EM_DASH,
  ariaLabel,
  // Collage multi-valeurs (Excel-like) : si le presse-papier contient plusieurs
  // valeurs (séparées par tabulation / saut de ligne), elles sont distribuées
  // sur les cellules suivantes au lieu d'un collage simple.
  onPasteMulti,
}) {
  const [draft, setDraft] = useState(null);

  const displayValue =
    draft !== null
      ? draft
      : (Number(value) ? String(Math.round(Number(value))) : '');

  const showAsFormatted = draft === null;
  const formatted = formatSwissAmount(value);

  return (
    <input
      type="text"
      inputMode="decimal"
      className={`billing-amount-input billing-amount-input--${align} ${className}`.trim()}
      disabled={disabled}
      aria-label={ariaLabel}
      placeholder={placeholder}
      value={showAsFormatted && formatted !== EM_DASH ? formatted : displayValue}
      onFocus={(e) => {
        setDraft(Number(value) ? String(Math.round(Number(value))) : '');
        // Select the full value so Tab-then-type immediately overwrites it
        // (workflow : saisie rapide des 12 mois au clavier).
        requestAnimationFrame(() => {
          const el = e.target;
          if (el && typeof el.select === 'function') el.select();
        });
      }}
      onChange={(e) => setDraft(e.target.value)}
      onPaste={(e) => {
        if (!onPasteMulti) return;
        const text = e.clipboardData?.getData('text') || '';
        const tokens = text
          .split(/[\t\r\n;]+/)
          .map((t) => t.trim())
          .filter((t) => t !== '');
        if (tokens.length > 1) {
          e.preventDefault();
          setDraft(null);
          onPasteMulti(tokens);
        }
      }}
      onBlur={() => {
        if (draft !== null) {
          const parsed = parseSwissAmount(draft);
          if (parsed !== Number(value || 0)) onCommit(parsed);
        }
        setDraft(null);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') e.target.blur();
        if (e.key === 'Escape') {
          setDraft(null);
          e.target.blur();
        }
      }}
    />
  );
}
